import { notifySessionInvalid } from './sessionAuth';

/**
 * Substitute API path placeholders. Replace fitness_profile_id (and friend_user_id) before user_id
 * because "user_id" is a substring of "fitness_profile_id". Keep in sync with spec/lib/user_endpoint_template_spec.rb.
 */
export function replaceUserEndpoint(template, { userId, id, participationId, friendUserId, fitnessProfileId }) {
	let url = String(template);
	// Replace longer tokens first — "user_id" is a substring of "fitness_profile_id".
	if (participationId != null) {
		url = url.replace(/:participation_id|participation_id/g, String(participationId));
	}
	if (fitnessProfileId != null) {
		url = url.replace(/:fitness_profile_id|fitness_profile_id/g, String(fitnessProfileId));
	}
	if (friendUserId != null) {
		url = url.replace(/:friend_user_id|friend_user_id/g, String(friendUserId));
	}
	url = url.replace(/:user_id|user_id/g, String(userId));
	if (id != null) {
		url = url.replace(/:id\b/g, String(id));
		url = url.replace(/\/id\//g, `/${id}/`);
		// Path helpers with id: :id emit a literal "id" segment (e.g. .../friendships/id, .../challenges/id/accept)
		url = url.replace(/\/id(\/|$)/g, `/${id}$1`);
	}
	return url;
}

const SESSION_EXPIRED_MESSAGE = 'Your session expired. Please sign in again.';

const SESSION_PROFILE_ERRORS = [
	'Set up or sign in with a fitness profile to manage this challenge.',
	'This action is not allowed for your signed-in fitness profile.'
];

export class ApiError extends Error {
	constructor(message, { status, sessionInvalid = false } = {}) {
		super(message);
		this.name = 'ApiError';
		this.status = status;
		this.sessionInvalid = sessionInvalid;
	}
}

function sessionInvalidFromResponse(response, data) {
	if (response.status === 401) return true;

	if (response.status === 403) {
		const msg = data.errors?.join?.(', ') || data.message || '';
		return SESSION_PROFILE_ERRORS.some((fragment) => msg.includes(fragment));
	}

	return false;
}

function throwSessionInvalid(message, status) {
	notifySessionInvalid(message);
	throw new ApiError(message, { status, sessionInvalid: true });
}

export async function apiJson(url, { method = 'GET', csrfToken, body } = {}) {
	const headers = { Accept: 'application/json', 'Content-Type': 'application/json' };
	if (csrfToken) headers['X-CSRF-Token'] = csrfToken;

	const response = await fetch(url, {
		method,
		headers,
		credentials: 'same-origin',
		body: body != null ? JSON.stringify(body) : undefined
	});
	const text = await response.text();
	let data = {};
	if (text) {
		try {
			data = JSON.parse(text);
		} catch {
			if (text.trimStart().startsWith('<') && (response.status === 401 || response.status === 403)) {
				throwSessionInvalid(SESSION_EXPIRED_MESSAGE, response.status);
			}
			throw new ApiError('Server returned an invalid response.', { status: response.status });
		}
	}
	if (!response.ok || data.success === false) {
		const msg =
			data.errors?.join?.(', ') || data.error || data.message || 'Request failed.';
		const sessionInvalid = sessionInvalidFromResponse(response, data);
		if (sessionInvalid) {
			throwSessionInvalid(SESSION_EXPIRED_MESSAGE, response.status);
		}
		throw new ApiError(msg, { status: response.status });
	}
	return data;
}
